import { ReactNode, useContext, useEffect } from "react";
import AuthContext from "../../store/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { getCookie } from "../../utils/cookies";
import { getRedirectPath } from "../../utils/logout";

interface TwoFactorAuthCheckerProps {
  children: ReactNode;
}

function TwoFactorAuthChecker( {children}: TwoFactorAuthCheckerProps): JSX.Element {
  const { _2FAuth, loggedPerson, isAuthenticated, set2FAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const twoFactorAuth = getCookie("2FAuth");
    if (twoFactorAuth) {
      set2FAuth(true);
    } else {
      set2FAuth(false);
    }
  }, [set2FAuth]);

  
  useEffect(() => {
    if (!loggedPerson || _2FAuth === null) {
      return;
    }
    const excludedRoutes = ['/two-factor-auth', '/login'];

    // If user is authenticated and tries to access auth pages, redirect them away
    if (excludedRoutes.includes(location.pathname) && loggedPerson && isAuthenticated) {
      const redirectPath = getRedirectPath();
      const targetPath = redirectPath || '/mywsg';
      
      // For users with 2FA enabled, they must have completed 2FA
      if (loggedPerson.twoFactorAuthentication?.status && _2FAuth) {
        navigate(targetPath, { replace: true });
      }
      // For users who have explicitly disabled 2FA (twoFactorAuthentication exists but status is false)
      else if (loggedPerson.twoFactorAuthentication && !loggedPerson.twoFactorAuthentication.status) {
        navigate(targetPath, { replace: true });
      }
      // Note: Users with undefined twoFactorAuthentication should be handled by handleAfterLogin
      // and redirected to /two-factor-acknowledgement, so we don't redirect them here
    }

    // If user has 2FA enabled but hasn't completed it, redirect to 2FA
    if (loggedPerson.twoFactorAuthentication?.status && !_2FAuth && isAuthenticated) {
      const searchParams = window.location.search;
      navigate(`/two-factor-auth${searchParams}`, { replace: true });
    }
  }, [_2FAuth, loggedPerson, isAuthenticated, navigate, location.pathname]);

  return <>{children}</>;
}

export default TwoFactorAuthChecker;
